import React, { useEffect, useState } from "react";
import Button from "../../components/primitives/button";
import {
  isGpcSignalActive,
  getStoredOptOut,
  setStoredOptOut,
} from "../../policies/privacy-preference-service";

// Public "Your Privacy Choices" page (reachable without an account) — the
// user-facing surface for the CPRA/GPC "do not sell or share" opt-out.
// Signed-in users also have this control in Settings, where it is stored on
// their account; this page records the preference for this browser.
export default function PrivacyChoices() {
  const gpcActive = isGpcSignalActive();
  const [optedOut, setOptedOut] = useState(() => getStoredOptOut());

  // An active GPC signal is itself a valid opt-out request (CPRA
  // §1798.135(e)) — record it so the preference persists in this browser
  // even if the signal is later turned off.
  useEffect(() => {
    if (gpcActive && !getStoredOptOut()) {
      setStoredOptOut(true);
      setOptedOut(true);
    }
  }, [gpcActive]);

  const effectiveOptOut = gpcActive || optedOut;

  const handleToggleOptOut = () => {
    const nextValue = !optedOut;
    setStoredOptOut(nextValue);
    setOptedOut(nextValue);
  };

  return (
    <main className="legalPage">
      <div className="legalPage__inner">
        <h1 className="legalPage__title">Your Privacy Choices</h1>
        <p className="legalPage__lead">
          We do not sell or share your personal information, and we do not run
          targeted advertising. You can still record a "do not sell or share"
          opt-out below — we honor it today by never selling or sharing your
          data, and it will block any such feature we might introduce in the
          future.
        </p>

        <section className="legalPage__section">
          <h2 className="legalPage__sectionTitle">Global Privacy Control</h2>
          <div className="legalPage__sectionContent">
            <p>
              Global Privacy Control (GPC) is a signal your browser or a
              browser extension can send to tell websites not to sell or share
              your personal information. We treat it as a valid opt-out
              request for this browser, and for your account when you are
              signed in.
            </p>
            <p aria-live="polite">
              <strong>
                GPC signal detected in this browser:{" "}
                {gpcActive ? "Yes" : "No"}
              </strong>
            </p>
            {gpcActive ? (
              <p>
                Your opt-out preference has been applied automatically —
                nothing else is required.
              </p>
            ) : null}
          </div>
        </section>

        <section className="legalPage__section">
          <h2 className="legalPage__sectionTitle">
            Do not sell or share my personal information
          </h2>
          <div className="legalPage__sectionContent">
            <p aria-live="polite">
              <strong>
                Your opt-out preference in this browser:{" "}
                {effectiveOptOut ? "Opted out" : "No opt-out recorded"}
              </strong>
            </p>
            {gpcActive ? (
              <p>
                Your browser's Global Privacy Control signal governs while it
                is active, so this preference stays opted out.
              </p>
            ) : (
              <Button variant="outline" size="sm" onClick={handleToggleOptOut}>
                {optedOut ? "Remove my opt-out" : "Opt out of sale or sharing"}
              </Button>
            )}
            <p>
              Signed-in members can also manage this preference under
              Settings, where it is stored on their account and applies on
              every device, including our mobile app. For access or deletion
              requests, email{" "}
              <a href="mailto:privacy@betterintentions.com">
                privacy@betterintentions.com
              </a>
              . See our <a href="/privacy-policy">Privacy Policy</a> for
              details.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
